// redux used for any region related
// region included telCode
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { MODULE_TYPE, OTP_VERIFICATION_METHOD } from '@utils/constants';

const initialState = {
    // raw data from JSON
    status: null,
    rawData: {
        registerFormSettingsJson: null,
    },
    processedData: {
        registerForm: null, // massaged value. Either normal register form / external register form
    },
    enable_dynamic_form: false,
};

// Define a common async function handler
const asyncThunkHandler = async (fn) => {
    try {
        return await fn();
    } catch (err) {
        console.error(err);
    }
};

/**
 * get telCodes data (e.g. "60": {...data})
 */
export const fetchRegisterFormSettingsJson = createAsyncThunk('register-slice/fetchRegisterFormSettingsJson', async (_, { getState }) => {
    const { registerReducer, authSettingsReducer } = getState();

    const enableOTP = authSettingsReducer?.moduleData?.[MODULE_TYPE?.REGISTRATION]?.authType === OTP_VERIFICATION_METHOD.OTP;

    if (registerReducer?.rawData?.registerFormSettingsJson) {
        return { registerFormSettingsJson: registerReducer.rawData.registerFormSettingsJson };
    }

    return asyncThunkHandler(async () => {
        if (registerReducer?.enable_dynamic_form) {
            let data = await window.SPL_JsonSettings.getRegisterFormSettingsJson();
            // add master switch handling
            // detect master switch open, detect third row and insert phoneEmail + authOTP

            data.registerForm.default = processOnRegisterFormSettingsJson(data?.registerForm?.default, enableOTP);

            return { registerFormSettingsJson: data };
        } else {
            return { registerFormSettingsJson: null };
        }
    });
});

function processOnRegisterFormSettingsJson(data, enableOTP) {
    if (enableOTP) {
        const phoneEmailFirstIndex = findingPhoneEmailFirstIndex(data); // data format will be {step, index}
        let positionIndex = 0;
        positionIndex = phoneEmailFirstIndex?.index;
        // filter out phone and email
        if (phoneEmailFirstIndex) {
            // remove the phone email first in step1 and step 2
            data = { ...data, step1: removePhoneEmail(data?.step1) };
            data = { ...data, step2: removePhoneEmail(data?.step2) };
            data = { ...data, default: removePhoneEmail(data?.default) };

            // adding back phoneEmailOTP for the index found just now
            if (phoneEmailFirstIndex?.step === 1 || phoneEmailFirstIndex?.step === 2) {
                // Determine which step to inject into (step1 or step2)
                const stepKey = phoneEmailFirstIndex.step === 1 ? 'step1' : 'step2';
                if (data[stepKey]?.length > 0) {
                    data = {
                        ...data,
                        [stepKey]: [
                            ...data[stepKey].slice(0, positionIndex),
                            { name: 'phoneEmail', isMandatory: false },
                            { name: 'authOTP', isMandatory: false },
                            ...data[stepKey].slice(positionIndex),
                        ],
                    };
                }
            }
        }
    } else {
        // function to remove phone email and auth otp if master switch no open

        // remove in step 1
        data = { ...data, step1: removeAuthComp(data?.step1) };
        // remove in step 2
        if (data?.step2 && data?.step2?.length > 0) {
            data = { ...data, step2: removeAuthComp(data?.step2) };
        }
    }
    return data;
}

function removePhoneEmail(list = []) {
    return list?.filter((item) => item.name !== 'phone' && item.name !== 'email') || [];
}

function findingPhoneEmailFirstIndex(list) {
    const findSmallerValidIndex = (step) => {
        const phoneIndex = step?.findIndex((item) => item.name === 'phone');
        const emailIndex = step?.findIndex((item) => item.name === 'email');

        // Return the smaller valid index, ignoring -1 (meaning not found)
        return phoneIndex >= 0 && emailIndex >= 0 ? Math.min(phoneIndex, emailIndex) : phoneIndex >= 0 ? phoneIndex : emailIndex;
    };

    // Check step 1
    const step1SmallerValidIndex = findSmallerValidIndex(list?.step1);
    if (step1SmallerValidIndex > -1) {
        return { step: 1, index: step1SmallerValidIndex };
    }

    // Check step 2 if step 1 doesn't have valid indexes
    const step2SmallerValidIndex = findSmallerValidIndex(list?.step2);
    if (step2SmallerValidIndex > -1) {
        return { step: 2, index: step2SmallerValidIndex };
    }

    // If neither step has a valid phone or email index return in step 1 index 2
    return { step: 1, index: 2 };
}

function removeAuthComp(list = []) {
    return list?.filter((item) => item.name !== 'phoneEmail' && item.name !== 'authOTP') || [];
}

export const registerSlice = createSlice({
    name: 'register',
    initialState,
    reducers: {
        setRegisterForm: (state, action) => {
            let registerForm = { ...action.payload };
            state.processedData.registerForm = registerForm;
        },
        setEnableDynamicForm: (state, action) => {
            state.enable_dynamic_form = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchRegisterFormSettingsJson.fulfilled, (state, action) => {
            state.rawData.registerFormSettingsJson = action?.payload?.registerFormSettingsJson;
            state.status = true;
        });
    },
});

export const { setRegisterForm, setEnableDynamicForm } = registerSlice.actions;

export default registerSlice.reducer;
